import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { useScenarioStore } from '../../pages/scenario-modeling/scenario-modeling.store';
import { ApiService } from '../../services/index';
import { useScenarioListener } from '../../sockets/scenarios.util';
import { selectedCompanyStorage } from '../../storage/company/selected-company.util';
import { ConversionSummary } from '../../types/scenario-modeling.types';
import { useAppQuery } from '../use-app-query.hook';
import { useInvalidateQueries } from '../use-invalidate-queries.hook';

const conversionSummaryBackgroundRequestSent = new Set<string>();

export const useConversionSummary = ({
  enabled = true,
  scenarioId,
}: {
  enabled?: boolean;
  scenarioId: string;
}) => {
  const queryClient = useQueryClient();
  const { invalidateQuery } = useInvalidateQueries(QueryKey.GET_CONVERSION_SUMMARY);
  const { setActionStateBlocked, isChangeActionsBlock } = useScenarioStore();
  const isQueryEnabled = Boolean(scenarioId && enabled && !isChangeActionsBlock);
  const {
    data: conversionSummary,
    error,
    isFetching,
    isLoading,
    isSuccess,
  } = useAppQuery<ConversionSummary>({
    queryKey: [QueryKey.GET_CONVERSION_SUMMARY, scenarioId],
    queryFn: () =>
      ApiService.get({
        endpoint: BackendRoute.SCENARIOS,
        routePath: [scenarioId, 'conversion-summary'],
      }),
    enabled: isQueryEnabled,
    retry: false,
    staleTime: 30_000,
    structuralSharing: false,
  });

  useEffect(() => {
    if (!isQueryEnabled || conversionSummaryBackgroundRequestSent.has(scenarioId)) return;

    conversionSummaryBackgroundRequestSent.add(scenarioId);
    ApiService.post(
      {
        endpoint: BackendRoute.SCENARIOS,
        routePath: [scenarioId, 'conversion-summary', 'request'],
      },
      { body: {} },
    ).catch(() => {});
  }, [isQueryEnabled, scenarioId]);

  const handleConversionSummaryUpdated = useCallback(
    ({ companyId }: { companyId: string }) => {
      const selectedCompanyId = selectedCompanyStorage.getState().companyId;
      if (!selectedCompanyId || companyId !== selectedCompanyId || !scenarioId) return;

      // Do NOT setActionStateBlocked(true) here: the blocked flag disables this very query,
      // so the invalidation below could never refetch and nothing would ever unblock —
      // freezing the details page until an unrelated calc ran (hurdle-modal timing bug).
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.GET_CONVERSION_SUMMARY, scenarioId],
      });
    },
    [queryClient, scenarioId],
  );

  useScenarioListener('conversion-summary-updated', handleConversionSummaryUpdated);

  useEffect(() => {
    setActionStateBlocked(false);
  }, [conversionSummary, setActionStateBlocked]);

  // 400: the scenario is refused as it stands (e.g. its SAFEs would own 100 % or more of the
  // company), so a cap table cannot be generated from it either.
  const isRefused = error instanceof HttpException && error.response.status === 400;

  return {
    // A failed refetch keeps the last calculated summary; a refused scenario has none, so those
    // numbers must not stay on screen as if they were current.
    conversionSummary: isRefused ? undefined : conversionSummary,
    invalidateQuery,
    isLoading: isLoading || isFetching,
    isRefused,
    isSuccess,
  };
};
